// Sign-in log tab: filterable stream of login attempts (success + failure)
// across every org. Mirrors FrenchPrep — window picker (24h / 7d / 30d),
// filter by All / Success / Failures, filter by reason, search by email.
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Search, CheckCircle2, XCircle, Monitor, Smartphone, Tablet, MapPin } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const WINDOWS = [
  { key: 24,        label: 'Last 24h' },
  { key: 24 * 7,    label: 'Last 7d' },
  { key: 24 * 30,   label: 'Last 30d' },
  { key: 24 * 90,   label: 'Last 90d' },
];

function DeviceIcon({ device }) {
  const cls = 'h-3.5 w-3.5';
  if (device === 'mobile') return <Smartphone className={cls} />;
  if (device === 'tablet') return <Tablet className={cls} />;
  return <Monitor className={cls} />;
}

export default function SignInsTab() {
  const [window, setWindow] = useState(24);
  const [result, setResult] = useState('all');   // all | success | failure
  const [reason, setReason] = useState('any');   // any | unknown_email | brute_force
  const [emailQ, setEmailQ] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { window_hours: window };
      if (result !== 'all') params.result = result;
      if (reason !== 'any') params.reason = reason;
      if (emailQ.trim()) params.email = emailQ.trim();
      const res = await api.adminSignIns(params);
      setRows(res.data.signins); setCounts(res.data.counts || {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load sign-ins.'); }
    setLoading(false);
  }, [window, result, reason, emailQ]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-6" data-testid="signins-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-slate-700" />
            <h3 className="font-display text-base font-semibold text-slate-900">Sign-in log</h3>
          </div>
          <div className="flex items-center gap-2">
            <select value={window} onChange={(e) => setWindow(Number(e.target.value))}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700"
              data-testid="signin-window">
              {WINDOWS.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
            </select>
            <button onClick={load} disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
            </button>
          </div>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-slate-500">
          Every login attempt (success and failure) across every org and role — including orphan rows where the typed email doesn&apos;t match any account.
          Paste any email below to see every attempt.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input value={emailQ} onChange={(e) => setEmailQ(e.target.value)}
              placeholder="Paste the user's email (or part of it) to see every attempt"
              className="h-9 pl-8" data-testid="signin-email-search" />
          </div>
          <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5">
            {['all', 'success', 'failure'].map((k) => (
              <button key={k} onClick={() => setResult(k)}
                className={cn('rounded px-3 py-1 text-xs font-medium capitalize',
                  result === k ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800')}
                data-testid={`signin-result-${k}`}>
                {k === 'failure' ? 'Failures' : k === 'success' ? 'Success' : 'All'}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="mono-stat">By reason</span>
          {[
            ['any', 'Any', null],
            ['unknown_email', `Unknown email · ${counts.unknown_email || 0}`, 'unknown_email'],
            ['brute_force', `Locked out (brute force) · ${counts.brute_force || 0}`, 'brute_force'],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setReason(k)}
              className={cn('rounded px-2.5 py-1 font-medium',
                reason === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
              data-testid={`signin-reason-${k}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 bg-white px-6 py-8 text-center text-sm text-slate-500">
            No sign-in attempts match these filters.
          </p>
        ) : rows.map((r, i) => (
          <div key={`${r.at}-${i}`} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm"
            data-testid={`signin-row-${i}`}>
            <div className={cn('grid h-8 w-8 place-items-center rounded-full',
              r.kind === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700')}>
              {r.kind === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-medium text-slate-900">{r.email || '—'}</p>
                {r.role && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">{r.role.replace('_', ' ')}</span>}
                <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                  r.kind === 'success' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800')}>
                  {r.reason.replace(/_/g, ' ')}
                </span>
                {!r.name && !r.role && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-700">Orphan</span>}
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <DeviceIcon device={r.device} /> {r.device || 'unknown'} · {r.os || '—'} · {r.browser || '—'}
                <MapPin className="ml-1 h-3 w-3" /> {r.ip || '—'}
                {r.org_name && <> · <span className="text-slate-700">{r.org_name}</span></>}
              </p>
            </div>
            <span className="whitespace-nowrap text-xs text-slate-400">{new Date(r.at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
